import { KeyFunctionMap, RxCollection, RxCollectionCreator, RxDocument, RxJsonSchema } from "rxdb"
import { WorkOrderCorrectiveItem, WorkOrderRegularItem } from "src/util/types/dataModels/workOrder"

//region Types

export interface WorkOrderCorrectiveCollectionMethod extends KeyFunctionMap {
  getWorkOrderCorrectiveReferences: () => Promise<Array<string>>
}

export interface WorkOrderRegularCollectionMethod extends KeyFunctionMap {
  getWorkOrderRegularReferences: () => Promise<Array<string>>
}

export type WorkOrderCorrectiveItemDocument = RxDocument<WorkOrderCorrectiveItem, KeyFunctionMap>
export type WorkOrderCorrectiveItemCollection = RxCollection<
  WorkOrderCorrectiveItem,
  KeyFunctionMap,
  WorkOrderCorrectiveCollectionMethod
>

export type WorkOrderRegularItemDocument = RxDocument<WorkOrderRegularItem, KeyFunctionMap>
export type WorkOrderRegularItemCollection = RxCollection<
  WorkOrderRegularItem,
  KeyFunctionMap,
  WorkOrderRegularCollectionMethod
>

//endregion

// base and WorkOrderCorrectiveItem are equivalent
export const workOrderBaseProperties: RxJsonSchema<WorkOrderCorrectiveItem>["properties"] = {
  id: { type: "string", maxLength: 36, uniqueItems: true },
  created_dt: { type: "string", maxLength: 64 },
  updated_dt: { type: "string", maxLength: 64 },
  reference: { type: "string", maxLength: 80 },
  designation: { type: "string", maxLength: 128 },
  start_dt: { type: "string", maxLength: 64 },
  deadline_dt: { type: "string", maxLength: 64 },
  concluded_dt: { type: "string", maxLength: 64 },
  concluded_by_user: { type: "string", maxLength: 36 },
  instructions: { type: "string" },
  daily_participants: { type: "number" },
  avg_minutes_per_work: { type: "number" },
  hours_to_complete: { type: "number" },
  daily_avg_work_hours: { type: "number" },
  work_domain: { ref: "work_domain_workspace", type: "string", maxLength: 36 },
  priority: { ref: "work_order_priority", type: "string", maxLength: 36 },
  procedure_revision: { type: "string", maxLength: 36 },
  assigned_users: {
    type: "array",
    items: {
      type: "string",
    },
  },
  accepted_alternative_procedure_revisions: {
    type: "array",
    items: {
      type: "string",
    },
  },
  attachments: {
    type: "array",
    items: {
      type: "string",
    },
  },
}

/**
 * Schema for work order corrective item collections
 */
export const workOrderCorrectiveItemSchema: RxJsonSchema<WorkOrderCorrectiveItem> = {
  title: "Work Order Corrective Item schema",
  description: "Collection of all work order corrective items",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: workOrderBaseProperties,
  required: [
    "id",
    "designation",
    "start_dt",
    "instructions",
    "work_domain",
    "priority",
    "assigned_users",
  ],
  indexes: ["designation", "work_domain"],
}

/**
 * Schema for work order regular item collections
 */
export const workOrderRegularItemSchema: RxJsonSchema<WorkOrderRegularItem> = {
  title: "Work Order Regular Item schema",
  description: "Collection of all work order regular items",
  version: 0,
  primaryKey: "id",
  type: "object",
  keyCompression: true,
  properties: {
    ...workOrderBaseProperties,
    items: {
      type: "array",
      items: {
        ref: "item",
        type: "string",
      },
    },
  },
  required: [
    "id",
    "designation",
    "start_dt",
    "instructions",
    "work_domain",
    "priority",
    "assigned_users",
    "items",
  ],
  indexes: ["designation", "work_domain"],
}

export const workOrderCorrectiveCollectionMethods: WorkOrderCorrectiveCollectionMethod = {
  async getWorkOrderCorrectiveReferences(
    this: WorkOrderCorrectiveItemCollection
  ): Promise<string[]> {
    const allDocs: WorkOrderCorrectiveItemDocument[] = await this.find().exec()
    return allDocs.flatMap((workOrder) => workOrder.reference ?? [])
  },
}

export const workOrderRegularCollectionMethods: WorkOrderRegularCollectionMethod = {
  async getWorkOrderRegularReferences(this: WorkOrderRegularItemCollection): Promise<string[]> {
    const allDocs: WorkOrderRegularItemDocument[] = await this.find().exec()
    return allDocs.flatMap((workOrder) => workOrder.reference ?? [])
  },
}

/**
 * A collection creator that can be passed to the database when setting up collections
 */
export const workOrderCorrectiveItemCollectionCreator: RxCollectionCreator = {
  schema: workOrderCorrectiveItemSchema,
  statics: workOrderCorrectiveCollectionMethods,
}

export const workOrderRegularItemCollectionCreator: RxCollectionCreator = {
  schema: workOrderRegularItemSchema,
  statics: workOrderRegularCollectionMethods,
}
